import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { Button, Icon } from '@efira/ui';

import { useBilling } from '../../api/queries';

/**
 * ВОЗВРАЩЕНИЕ С ПЛАТЁЖНОЙ СТРАНИЦЫ.
 *
 * Две страницы - «оплатил» и «передумал», - и обе существуют ДО подключения
 * провайдера намеренно: при подключении они ломаются первыми. Провайдер
 * возвращает человека по адресу, который мы ему дали; если адреса нет,
 * человек упирается в 404 сразу после того, как заплатил, - это худшая
 * минута продукта, и чинить её задним числом нельзя.
 *
 * ГЛАВНОЕ ПРАВИЛО УСПЕХА: СТРАНИЦА НИЧЕГО НЕ ВЫДАЁТ. Права даёт уведомление
 * провайдера, проверенное подписью (`payments/lifecycle.ts`). Страница
 * возврата - это то, что видит человек в браузере, и поверить ей нельзя:
 * её адрес можно открыть руками. Поэтому она только СПРАШИВАЕТ у нашего
 * сервера, что он знает, и показывает ответ.
 *
 * Уведомление приходит не мгновенно: пока тариф не сменился, страница ждёт и
 * говорит, чего ждёт, - а не показывает «поздравляем» над бесплатным тарифом.
 */

/** Сколько ждать уведомления, прежде чем предложить написать нам. */
const WAIT_SECONDS = 60;

export function BillingDone() {
  const billing = useBilling();
  const [waited, setWaited] = useState(0);
  const pro = billing.data?.plan.id === 'pro';

  useEffect(() => {
    if (pro) return undefined;

    const timer = window.setInterval(() => {
      setWaited((value) => value + 3);
      void billing.refetch();
    }, 3000);

    return () => {
      window.clearInterval(timer);
    };
  }, [pro, billing]);

  return (
    <div data-billing-return="done" className="flex flex-col gap-4">
      <header className="flex flex-col gap-1">
        <h2 className="text-foreground flex items-center gap-2 text-xl font-semibold">
          <Icon name={pro ? 'success' : 'clock'} size="md" className={pro ? 'text-primary' : ''} />
          {pro ? 'Pro включён' : 'Ждём подтверждение оплаты'}
        </h2>
        <p data-billing-return-text className="text-foreground/80 text-sm">
          {/* Права НЕ перечисляются здесь списком: их состав живёт на
              сервере (`world/plan.ts`), и второй список в разметке
              разойдётся с ним молча. */}
          {pro
            ? 'Тариф Pro уже действует — что в него входит, показывает окно тарифа.'
            : 'Платёжный сервис ещё не сообщил нам о платеже. Обычно это занимает несколько секунд — страница обновится сама.'}
        </p>
      </header>

      {!pro && waited >= WAIT_SECONDS && (
        /* Ждать молча дольше минуты нельзя: человек заплатил и не понимает,
           что происходит. Называем, что делать дальше, и не обвиняем его. */
        <p
          data-billing-return="slow"
          className="border-warning/50 bg-warning/10 rounded-lg border px-4 py-3 text-sm"
        >
          Подтверждение идёт дольше обычного. Если списание прошло, тариф включится, когда придёт
          подтверждение. Напишите на hello@efira.live — включим руками и разберёмся с платежом.
        </p>
      )}

      <div className="flex flex-wrap gap-2">
        <Button asChild variant="primary" size="s">
          <Link to="/app/settings/billing">Открыть «Оплату»</Link>
        </Button>
        <Button asChild variant="ghost" size="s">
          <Link to="/app">В консоль</Link>
        </Button>
      </div>
    </div>
  );
}

export function BillingCancelled() {
  const [params] = useSearchParams();
  /* Причину отказа провайдер может назвать - а может и не назвать. Своих
     догадок здесь нет: не назвал, значит человек просто передумал. */
  const reason = params.get('reason');
  /* Пометку из адреса НЕ приписываем платёжному сервису: адрес открывается
     руками, и любая ссылка вставила бы в наш интерфейс чужую фразу с
     авторитетом сервиса («карта заблокирована банком»). */
  const hasReason = reason !== null && reason !== '';

  return (
    <div data-billing-return="cancelled" className="flex flex-col gap-4">
      <header className="flex flex-col gap-1">
        <h2 className="text-foreground text-xl font-semibold">Оплата не завершена</h2>
        <p data-billing-return-text className="text-foreground/80 text-sm">
          {/* «По нашим данным»: страница возврата не знает, что произошло
              у платёжного сервиса, - она знает только то, что видим мы. */}
          {hasReason
            ? `По нашим данным списания нет; тариф не изменился. В адресе возврата пришла пометка «${String(reason)}».`
            : 'По нашим данным списания нет; тариф не изменился. Вернуться к оформлению можно в любой момент.'}
        </p>
      </header>

      <div className="flex flex-wrap gap-2">
        <Button asChild variant="primary" size="s">
          <Link to="/app/plan?from=billing">Вернуться к тарифу</Link>
        </Button>
        <Button asChild variant="ghost" size="s">
          <Link to="/app">В консоль</Link>
        </Button>
      </div>
    </div>
  );
}
